import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Star, Download, TrendingUp, Award, Plus, Eye } from 'lucide-react';
import { MarketplaceItemCard } from '../../components/marketplace/MarketplaceItemCard';
import { MarketplaceFilters } from '../../components/marketplace/MarketplaceFilters';
import { PublishWorkflowModal } from '../../components/marketplace/PublishWorkflowModal';
import { useMarketplace } from '../../hooks/useMarketplace';
import { MarketplaceItem, MarketplaceFilters as FiltersType } from '../../types/marketplace';
import { useAuthStore } from '../../stores/demo-auth';

const PAGE_SIZES: Record<'grid' | 'list', number> = { grid: 20, list: 10 };

export const MarketPlace = () => {
  const { user } = useAuthStore();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FiltersType>({
    category: '',
    tags: [],
    priceRange: { min: 0, max: Infinity },
    minRating: 0,
    type: '',
    sortBy: 'popularity'
  });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'all' | 'trending' | 'featured'>('all');

  const {
    items,
    trendingItems,
    featuredItems,
    categories,
    loading,
    error,
    totalPages,
    currentPage,
    searchItems,
    publishItem,
    downloadItem
  } = useMarketplace();

  useEffect(() => {
    searchItems({
      query: searchQuery,
      ...filters,
      pagination: { page: 1, limit: PAGE_SIZES[viewMode] }
    });
  }, [searchQuery, filters, viewMode, searchItems]);

  const handlePublishWorkflow = async (workflowData: any) => {
    try {
      await publishItem(workflowData);
      setShowPublishModal(false);
      // Refresh the marketplace
      searchItems({ query: searchQuery, ...filters, pagination: { page: 1, limit: 20 } });
    } catch (error) {
      console.error('Failed to publish workflow:', error);
    }
  };

  const handleDownload = async (item: MarketplaceItem) => {
    try {
      await downloadItem(item.id);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const getCurrentItems = () => {
    switch (selectedTab) {
      case 'trending':
        return trendingItems;
      case 'featured':
        return featuredItems;
      default:
        return items;
    }
  };

  return (
    <div className="container-fluid py-2 overflow-auto">
      {/* Header */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex align-align-items-center justify-content-between mb-3">
            <div>
              <h1 className="h3 mb-1">🏪 Community Marketplace</h1>
              <p className="text-muted mb-0">
                Discover and install workflows, interfaces, and templates from our community
              </p>
            </div>
            <div className="text-end row">
              <div className="col-auto">
                <div className="btn-group" role="group">
                  <button 
                    type="button" 
                    className={`btn ${viewMode === 'grid' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => setViewMode('grid')}
                  >
                    ⊞
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${viewMode === 'list' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => setViewMode('list')}
                  >
                    ☰
                  </button>
                </div>
              </div>
              <div className='col-auto'>
                <button
                  onClick={() => setShowPublishModal(true)} className="btn btn-outline-primary">
                  📤 Publish Your Work
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row mb-3">
        {/* Search Bar */}
        <div className="col-3">
          <div className="input-group">
            <span className="input-group-text">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search workflows, nodes, and templates..."
              className="form-control"
            />
          </div>
        </div>
        <div className="col-auto">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className='form-control'
            title={showFilters ? 'Hide filters' : 'Show filters'}
          >
            <Filter className="w-1 h-1" />
          </button>
        </div>
        <div className='col'></div>
        <div className="col-auto">
          {/* Category Tabs */}
          <div className="d-flex gap-2" role="tablist">
            {[
              { key: 'all', label: 'All Items', icon: null },
              { key: 'trending', label: 'Trending', icon: TrendingUp },
              { key: 'featured', label: 'Featured', icon: Star }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setSelectedTab(key as any)}
                className={`btn ${
                      selectedTab === key ? 'btn-primary' : 'btn-outline-secondary'
                    }`}
              >
                {Icon && <Icon className="" />}
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row gx-3">
        {/* Filters Sidebar */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, x: -300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -300 }}
              className="col-lg-3 d-none d-lg-block"
            >
              <MarketplaceFilters
                filters={filters}
                categories={categories}
                onChange={setFilters}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Items Grid */}
        <div className={showFilters ? 'col-lg-9' : 'col-12'}>
          {loading ? (
            <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="col">
                  <div className="card h-100 placeholder-glow">
                    <div className="card-img-top bg-secondary" style={{ height: '12rem' }}></div>
                    <div className="card-body">
                      <div className="placeholder col-8 mb-2"></div>
                      <div className="placeholder col-5 mb-2"></div>
                      <div className="placeholder col-12"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-danger">Error loading marketplace</div>
                <div className="text-muted">{error}</div>
              </div>
            ) : getCurrentItems().length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-3" style={{ fontSize: '4rem', opacity: 0.3 }}>🏪</div>
                  <h3 className="text-muted">No Workflows</h3>
                  <p className="text-muted mb-3">
                    No Applications have been created yet or have been found with selected filters
                  </p>

                  <Link to="/workflows" className="btn btn-primary">
                    Explore Workflows
                  </Link>
                </div>
            ) : (
              <div className='row'>
                <AnimatePresence>
                  {getCurrentItems().map((item) => (
                    <MarketplaceItemCard
                      key={item.id}
                      viewMode={viewMode}
                      item={item}
                      onDownload={() => handleDownload(item)}
                      onPreview={(item) => {
                        alert(`Opening preview for "${item.title}"...`);
                      }}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="justify-content-center mt-2">
                <div className="">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => searchItems({
                        query: searchQuery,
                        ...filters,
                        pagination: { page: i + 1, limit: PAGE_SIZES[viewMode] }
                      })}
                      className={`btn me-1 ${
                        currentPage === i + 1
                          ? 'btn-primary'
                          : 'btn-outline-secondary'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      {/* Call to Action */}
      <div className="mt-4 pb-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3 p-4 bg-primary text-white text-center"
        >
          <h3 className="fw-bold mb-3">🚀 Share Your Creations</h3>
          <p className="mb-3 mx-auto">
            Have you built an amazing workflow or interface? Share it with the community and earn from downloads!
          </p>
          <div className="row justify-content-center">
            <div className='col-auto'>
              <button
                onClick={() => setShowPublishModal(true)}
                className="form-control"
              >
                📤 Publish Workflow
              </button>
            </div>
            <div className='col-auto'>
              <button
                onClick={() => alert('Interface builder coming soon!')}
                className="form-control"
              >
                🎨 Publish Interface
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Publish Modal */}
      <PublishWorkflowModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onPublish={handlePublishWorkflow}
      />
    </div>
  );
};

export default MarketPlace;